import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import { ApiHelper as APIHelper } from "@datanac/datanac-api-toolkit/dist/api-helper";
import { useDialog } from "@datanac/datanac-mui-toolkit";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import DeleteForeverOutlined from "@mui/icons-material/DeleteForeverOutlined";
import EastOutlinedIcon from "@mui/icons-material/EastOutlined";
import { Button, Divider, useMediaQuery } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { _sessionManager, UsersApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import { InsuranceIcon } from "components/Icons/Icons";
import { MODULE_INSURANCE } from "components/Menu/config/constants";
import TilleyDataGrid from "components/shared/DataGrid/TilleyDataGrid";
import WorkspaceActionBar from "components/Workspaces/WorkspaceActionBar";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import dayjs from "dayjs";
import { useSnackbar } from "notistack";
import { formatNumber, formatPercentage, formatPrice } from "utility/formatters";
import { claimPaymentAmount } from "./InsuranceService";

const pageSizes = [5, 10, 15, 20, 25, 50, 100];
const rowHeight = 50;

const formatDate = (value, format = "MM/DD/YYYY") => (dayjs(value).isValid() ? dayjs(value).format(format) : "");

export default function InsuranceClaimsWorkspace() {
  const { globalState } = useContext(AppContext);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const { component: confirmDialogComponent, actions: confirmDialogActions } = useDialog();

  const [insuranceClaims, setInsuranceClaims] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const handleError = useCallback(
    (err) => {
      console.warn(err);
      enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
    },
    [enqueueSnackbar]
  );

  const loadInsuranceClaims = useCallback(async () => {
    setIsLoading(true);
    try {
      const producerFilter = {
        producer_token: globalState.producer_token,
        is_active: true,
        module: MODULE_INSURANCE,
        reinsurance_year: globalState.crop_year,
      };

      const rows = await UsersApiHelper.users_search("InsurancePlanClaim", producerFilter, globalState);
      setInsuranceClaims((rows || []).map((row) => ({ ...row, payment_amount: claimPaymentAmount(row) })));
    } catch (err) {
      setInsuranceClaims([]);
      handleError(err);
    } finally {
      setIsLoading(false);
    }
  }, [globalState, handleError]);

  useEffect(() => {
    if (globalState?.producer_token) {
      loadInsuranceClaims();
    }
  }, [globalState?.producer_token, loadInsuranceClaims]);

  const handleDeleteOnClick = (claim) => {
    confirmDialogActions.confirm({
      title: "Delete this entry?",
      prompt: "Are you sure you want to delete this entry?",
      promptDetail: null,
      callback: () => handleDeleteClaim(claim),
    });
  };

  const handleDeleteClaim = async (claim) => {
    try {
      const api = new APIHelper({
        baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
        config: {},
        sessionManager: _sessionManager,
        _instance: null,
      });

      await api.callPost(
        "InsurancePlanClaim",
        {
          module: MODULE_INSURANCE,
          InsurancePlanClaim: [{ ...claim, is_active: false }],
          producer_token: globalState?.producer_token,
        },
        globalState
      );
      loadInsuranceClaims();
    } catch (err) {
      console.warn(err);
      enqueueSnackbar(err?.detail || err?.message || err, { preventDuplicate: true, variant: "error" });
    }
  };

  const COLUMNS = useMemo(
    () => [
      { field: "location_state_name", headerName: "State", flex: 1 },
      { field: "location_county_name", headerName: "County", flex: 1 },
      { field: "commodity_name", headerName: "Commodity", flex: 1 },
      { field: "practice_name", headerName: "Practice", flex: 1 },
      { field: "insurance_plan_name", headerName: "Insurance Plan", flex: 1 },
      {
        field: "first_damage_date",
        headerName: "First Damage Date",
        flex: 1,
        valueFormatter: (value) => formatDate(value),
      },
      {
        field: "first_damage_cause_name",
        headerName: "First Damage Cause Name",
        flex: 1,
      },
      {
        field: "first_damage_percent",
        headerName: "First Damage Percent %",
        flex: 1,
        valueFormatter: (value) => formatPercentage(value, 0),
      },
      {
        field: "second_damage_cause_name",
        headerName: "Second Damage Cause Name",
        flex: 1,
      },
      { field: "stage_name", headerName: "Stage Name", flex: 1 },
      {
        field: "insured_loss_signature_date",
        headerName: "Insured Loss Date",
        flex: 1,
        valueFormatter: (value) => formatDate(value),
      },
      { field: "aip_contact_name", headerName: "AIP Contact Name", flex: 1 },
      {
        field: "adjuster_signature_date",
        headerName: "Adjuster Date",
        flex: 1,
        valueFormatter: (value) => formatDate(value),
      },
      {
        field: "production_to_count_quantity",
        headerName: "Production to Count Quantity",
        flex: 1,
        valueFormatter: (value) => formatNumber(value, 0),
      },
      {
        field: "payment_amount",
        headerName: "Payment Amount",
        flex: 1,
        valueFormatter: (value) => formatPrice(value, 2),
      },
      {
        field: "payment_date",
        headerName: "Payment Date",
        flex: 1,
        valueFormatter: (value) => formatDate(value),
      },
      { field: "note", headerName: "Note", flex: 1 },
      {
        field: "actions",
        headerName: "Actions",
        type: "actions",
        headerAlign: "right",
        align: "right",
        width: 100,
        sortable: false,
        getActions: (params) => [
          <GridActionsCellItem
            key="edit"
            label="Edit"
            icon={<EastOutlinedIcon />}
            onClick={() => navigate(`/insurance/claims/${params.row.id}`)}
          />,
          <GridActionsCellItem
            key="delete"
            icon={<DeleteForeverOutlined />}
            label="Delete"
            data-testid={`delete-insurance-claim-${params?.row.first_damage_cause_name}`}
            onClick={() => handleDeleteOnClick(params?.row)}
          />,
        ],
      },
    ],
    [navigate]
  );

  return (
    <>
      <WorkspaceHeader
        title="Claims"
        category={{
          title: "Insurance",
          icon: <InsuranceIcon />,
        }}
        breadCrumbs={[
          { title: "Scenarios", path: "/insurance" },
          { title: "Claims", path: "/insurance/claims" },
        ]}
      />

      <TilleyDataGrid
        rows={insuranceClaims}
        columns={COLUMNS}
        pageSizeOptions={pageSizes}
        defaultPageSize={isMobile ? 5 : 10}
        gridKey="insurance-claims-global"
        getRowId={(row) => row.id}
        rowHeight={rowHeight}
        isLoading={isLoading}
        sx={{ maxWidth: "99%", boxSizing: "border-box" }}
      />

      <Divider />

      <WorkspaceActionBar
        content={
          <Button variant="contained" onClick={() => navigate("/insurance/claims/0")}>
            <AddCircleOutline sx={{ mr: 1 }} />
            {!isMobile && "Add "}Claim
          </Button>
        }
      />

      {confirmDialogComponent}
    </>
  );
}
